import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import WorkspacePremiumOutlined from "@mui/icons-material/WorkspacePremiumOutlined";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";

import logo from "../../assets/logo.png";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IExhibition } from "../../interfaces/IExhibition";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import { downloadBase64Pdf } from "../../utils/downloadPdf";
import { JudgeCard, JudgeCardMax, JudgeCardMin } from "./FollowUp.styles";

interface IEvaluation {
  id: string;
  userId: string;
  judgmentCriteriaId: string;
  score: number | null;
  plantId: string;
  coordinatorScore: number | null;
  user: {
    id: string;
    name: string;
    username: string;
    email: string;
    password: string;
    typeId: number;
    isActive: boolean;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface ITie {
  categoryName: string;
  categorySequence: number;
  score: number;
  plants: Array<{ number: string | number; name: string }>;
}

export const FollowUp: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;
  const [exhibition, setExhibition] = useState<IExhibition | null>(null);
  const [loading, setLoading] = useState(false);
  const [tiesDialogOpen, setTiesDialogOpen] = useState(false);
  const [ties, setTies] = useState<ITie[]>([]);
  const [judgesDurations, setJudgesDurations] = useState<Array<{
    judgeId: string;
    judgeName: string;
    start: string | null;
    end: string | null;
    duration: string;
  }>>([]);
  const toastr = useToastr();
  const { user } = useAuth();

  const handleListExhibition = useCallback(async () => {
    const id = window.location.pathname.replace("/follow-up/", "");
    if (!id) return;

    setLoading(true);
    try {
      const response = await api.get(
        `/exhibitions/complete/${id}?allJudges=true`,
      );
      setExhibition(response.data);

      const judgesResponse = await api.get(`/exhibitions/${id}/evaluation/judges`);
      setJudgesDurations(judgesResponse.data);
    } catch (error: any) {
      setExhibition(null);
      setJudgesDurations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddCoordinatorScore = useCallback(
    async (action: "add" | "remove", evaluationId: string) => {
      setLoading(true);
      try {
        await api.post(`/evaluations/coordinator/${evaluationId}`, {
          action,
        });
        await handleListExhibition(); // Recarrega os dados ao invés de usar response.data
      } catch (error: any) {
        toastr.error(
          error.response?.data?.message ||
            "Erro ao atualizar pontuação do coordenador",
        );
      } finally {
        setLoading(false);
      }
    },
    [toastr, handleListExhibition],
  );

  const handleGenerateJudgeCertificate = useCallback(
    async (judgeId: string) => {
      const exhibitionId = exhibition?.id;
      if (!exhibitionId || !judgeId) return;
      setLoading(true);
      try {
        const response = await api.post(
          `/exhibitions/${exhibitionId}/evaluation/judges/certificates`,
          { judgeIds: [judgeId] },
        );
        const { certificates } = response.data as {
          certificates: { filename: string; base64: string }[];
        };
        if (certificates?.length) {
          downloadBase64Pdf(certificates[0].base64, certificates[0].filename);
          toastr.success("Certificado gerado com sucesso!");
        }
      } catch (error: any) {
        toastr.error(
          error?.response?.data?.message || "Erro ao gerar certificado do juiz",
        );
      } finally {
        setLoading(false);
      }
    },
    [exhibition?.id, toastr],
  );

  useEffect(() => {
    handleListExhibition();
  }, [handleListExhibition]);

  useEffect(() => {
    if (!exhibition) return;
    const detected = detectTies(getJudgmentsByCategory());
    setTies(detected);
    if (detected.length > 0) setTiesDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exhibition]);

  // Utilitários de ordenação
  const sortCategories = (categories: any[] = []) =>
    categories
      .slice()
      .sort(
        (a, b) => (a.category?.sequence ?? 0) - (b.category?.sequence ?? 0),
      );

  const sortPlants = (plants: any[] = []) =>
    plants.slice().sort((a, b) => Number(a.number) - Number(b.number));

  const sortCriteria = (criteria: any[] = []) =>
    criteria
      .slice()
      .sort(
        (a, b) => (a.criteria?.sequence ?? 0) - (b.criteria?.sequence ?? 0),
      );

  const computeFinalScore = (plant: any): number => {
    const evaluationsByJudge: Record<string, IEvaluation[]> = {};
    (plant.evaluations || []).forEach((ev: IEvaluation) => {
      if (!evaluationsByJudge[ev.userId]) evaluationsByJudge[ev.userId] = [];
      evaluationsByJudge[ev.userId].push(ev);
    });

    const judgeTotals = Object.values(evaluationsByJudge).map(evals =>
      evals.reduce(
        (sum, ev) => sum + (ev.score ?? 0) + (ev.coordinatorScore ?? 0),
        0,
      ),
    );

    if (judgeTotals.length === 0) return 0;
    if (judgeTotals.length > 3) {
      const sorted = [...judgeTotals].sort((a, b) => a - b);
      const trimmed = sorted.slice(1, -1);
      return trimmed.reduce((sum, v) => sum + v, 0) / trimmed.length || 0;
    }
    return judgeTotals.reduce((sum, v) => sum + v, 0) / judgeTotals.length;
  };

  const detectTies = (categories: any[]): ITie[] => {
    const result: ITie[] = [];
    categories.forEach(cat => {
      const scoreMap: Record<string, any[]> = {};
      cat.plants.forEach((plant: any) => {
        const score = computeFinalScore(plant);
        if (score <= 0) return;
        const key = score.toFixed(4);
        if (!scoreMap[key]) scoreMap[key] = [];
        scoreMap[key].push({ plant, score });
      });

      Object.values(scoreMap).forEach(group => {
        if (group.length > 1) {
          result.push({
            categoryName: cat.category?.name || "Sem categoria",
            categorySequence: cat.category?.sequence ?? 0,
            score: group[0].score,
            plants: group.map(({ plant }) => ({
              number: plant.number,
              name: plant.name,
            })),
          });
        }
      });
    });
    return result;
  };

  // Agrupa julgamentos por categoria
  const getJudgmentsByCategory = () => {
    if (!exhibition?.judgments) return [];
    const grouped: Record<string, any> = {};
    exhibition.judgments.forEach(judgment => {
      const catId = judgment.category?.id || "sem-categoria";
      if (!grouped[catId]) {
        grouped[catId] = {
          category: judgment.category,
          plants: [],
          judgmentCriteria: judgment.judgmentCriteria,
        };
      }
      // Junta todas as plantas dessa categoria
      grouped[catId].plants = grouped[catId].plants.concat(
        judgment.plants || [],
      );
      grouped[catId].judgmentCriteria = judgment.judgmentCriteria;
    });
    return sortCategories(Object.values(grouped));
  };

  return (
    <>
      <Dialog
        open={tiesDialogOpen}
        onClose={() => setTiesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Empate de notas detectado
        </DialogTitle>
        <DialogContent dividers>
          {ties.map((tie, idx) => (
            <Box key={idx} sx={{ mb: idx < ties.length - 1 ? 2 : 0 }}>
              <Typography variant="subtitle2" fontWeight={700} color="primary">
                {tie.categorySequence} - {tie.categoryName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, color: "#666" }}>
                Nota final: <strong>{tie.score.toFixed(2)}</strong>
              </Typography>
              {tie.plants.map((plant, pIdx) => (
                <Typography key={pIdx} variant="body2" sx={{ pl: 1 }}>
                  • {plant.number} — {plant.name}
                </Typography>
              ))}
              {idx < ties.length - 1 && <Divider sx={{ mt: 1.5 }} />}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTiesDialogOpen(false)}
            variant="contained"
            size="small"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {loading && <BackdropCustom />}
      <Box
        sx={{
          p: { xs: 1, md: 2 },
          width: "90vw",
          maxWidth: "90vw",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            mb: 2,
            gap: 1,
          }}
        >
          <Avatar
            src={logo}
            alt="Logo"
            sx={{ width: 70, height: 70, mb: 1, bgcolor: "transparent" }}
            variant="square"
          />
          <Typography variant="h5" fontWeight={700} align="center">
            Relatório de acompanhamento
          </Typography>
          <Typography variant="h6" fontWeight={500} align="center">
            {exhibition?.name}
          </Typography>
          <Typography variant="subtitle1" fontWeight={400} align="center">
            Data: {dayjs(exhibition?.date).format("DD/MM/YYYY")}
          </Typography>
        </Box>
        {exhibition &&
          getJudgmentsByCategory().map((cat, idx) => (
            <Paper
              key={cat.category?.id || idx}
              sx={{
                p: { xs: 1.5, md: 2 },
                mb: 2,
                background: "#fafbfc",
                boxShadow: "none",
                borderRadius: 1,
              }}
              elevation={0}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                color="primary"
                sx={{ mb: 1 }}
              >
                {cat.category?.sequence} -{" "}
                {cat.category?.name || "Sem categoria"}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {sortPlants(cat.plants).length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, fontStyle: "italic" }}
                >
                  Não houveram plantas nesta categoria.
                </Typography>
              ) : (
                sortPlants(cat.plants).map(plant => {
                  // Agrupa avaliações por juiz
                  const evaluationsByJudge: Record<string, IEvaluation[]> = {};

                  (plant.evaluations || []).forEach((ev: IEvaluation) => {
                    if (!evaluationsByJudge[ev.userId]) {
                      evaluationsByJudge[ev.userId] = [];
                    }
                    evaluationsByJudge[ev.userId].push(ev);
                  });

                  const judges = Object.keys(evaluationsByJudge);

                  const finalScore = computeFinalScore(plant);
                  const hasEvaluations = (plant.evaluations || []).length > 0;

                  return (
                    <Box
                      key={plant.id}
                      sx={{
                        mb: 2,
                        pl: { xs: 0, md: 1 },
                        py: 1,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body1" fontWeight={600}>
                          {plant.number} - {plant.name}
                        </Typography>
                        {hasEvaluations && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#1976d2",
                              fontWeight: 700,
                              backgroundColor: "#e3f2fd",
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Média: {finalScore.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                      {judges.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Nenhuma avaliação registrada para esta planta.
                        </Typography>
                      )}
                      {/* Container flex para os cards dos juízes */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 2,
                          mt: 1,
                        }}
                      >
                        {judges
                          .map(judgeId => {
                            const judgeEvals = evaluationsByJudge[judgeId];
                            // Cria um mapa de avaliações por critério para facilitar a busca
                            const evalMap: Record<string, IEvaluation> = {};
                            judgeEvals.forEach(evaluation => {
                              evalMap[evaluation.judgmentCriteriaId] =
                                evaluation;
                            });

                            const sortedEvals = sortCriteria(
                              cat.judgmentCriteria,
                            ).map(judgmentCriteria => {
                              const evaluation = evalMap[judgmentCriteria.id];
                              return {
                                judgmentCriteria,
                                evaluation,
                                score: evaluation ? evaluation.score : null,
                                coordinatorScore: evaluation
                                  ? evaluation.coordinatorScore
                                  : null,
                                judge:
                                  evaluation?.user?.name ||
                                  judgeEvals[0]?.user?.name ||
                                  "Juiz",
                              };
                            });

                            const total = sortedEvals.reduce(
                              (sum: number, item) =>
                                sum +
                                ((item.score ?? 0) +
                                  (item.coordinatorScore ?? 0)),
                              0,
                            );

                            return { judgeId, sortedEvals, total };
                          })
                          .sort((a, b) => b.total - a.total)
                          .map(({ judgeId, sortedEvals, total }, idx, arr) => {
                            let isMax = false;
                            let isMin = false;
                            // Só pinta maior e menor se houver mais de 3 juízes
                            if (arr.length > 3) {
                              isMax = idx === 0;
                              isMin = idx === arr.length - 1;
                            }
                            const CardComponent = isMax
                              ? JudgeCardMax
                              : isMin
                                ? JudgeCardMin
                                : JudgeCard;

                            return (
                              <CardComponent
                                key={judgeId}
                                style={{
                                  minWidth: 200,
                                  maxWidth: 250,
                                  flex: "1 1 200px",
                                  padding: "16px",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{ mt: 0.5 }}
                                >
                                  Juiz: {sortedEvals[0]?.judge}
                                </Typography>
                                {sortedEvals.map(item => (
                                  <Box
                                    key={item.judgmentCriteria.id}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.75rem",
                                        lineHeight: 1.2,
                                        flex: 1,
                                      }}
                                    >
                                      {item.judgmentCriteria.criteria?.sequence}{" "}
                                      - {item.judgmentCriteria.criteria?.name}:
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                      >
                                        {item.score !== null ? item.score : "-"}
                                      </Typography>
                                      {item.coordinatorScore !== null &&
                                        item.coordinatorScore > 0 && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "#2e7d32",
                                              fontWeight: "bold",
                                              backgroundColor: "#e8f5e8",
                                              padding: "2px 6px",
                                              borderRadius: "4px",
                                              fontSize: "0.7rem",
                                              minWidth: "20px",
                                              textAlign: "center",
                                            }}
                                          >
                                            +{item.coordinatorScore}
                                          </Typography>
                                        )}
                                      {item.evaluation &&
                                        (item.coordinatorScore ?? 0) > 0 && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "#666",
                                              fontWeight: "bold",
                                              marginLeft: 0.5,
                                            }}
                                          >
                                            ={" "}
                                            {(item.score ?? 0) +
                                              (item.coordinatorScore ?? 0)}
                                          </Typography>
                                        )}
                                      {item.evaluation &&
                                        !exhibition.finished && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              marginLeft: 0.5,
                                              gap: 0.2,
                                            }}
                                          >
                                            {(item.coordinatorScore ?? 0) >
                                              0 && (
                                              <IconButton
                                                aria-label="remover ponto do coordenador"
                                                size="small"
                                                sx={{
                                                  color: "#d32f2f",
                                                  padding: "2px",
                                                  "&:hover": {
                                                    backgroundColor: "#ffebee",
                                                  },
                                                }}
                                                onClick={() => {
                                                  handleAddCoordinatorScore(
                                                    "remove",
                                                    item.evaluation!.id,
                                                  );
                                                }}
                                              >
                                                <RemoveIcon fontSize="inherit" />
                                              </IconButton>
                                            )}

                                            <IconButton
                                              aria-label="adicionar ponto do coordenador"
                                              size="small"
                                              sx={{
                                                color: "#2e7d32",
                                                padding: "2px",
                                                "&:hover": {
                                                  backgroundColor: "#e8f5e8",
                                                },
                                              }}
                                              onClick={() => {
                                                handleAddCoordinatorScore(
                                                  "add",
                                                  item.evaluation!.id,
                                                );
                                              }}
                                            >
                                              <AddIcon fontSize="inherit" />
                                            </IconButton>
                                          </Box>
                                        )}
                                    </Box>
                                  </Box>
                                ))}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mt: 0.5,
                                    ml: 1,
                                    fontWeight:
                                      isMax || isMin ? 700 : undefined,
                                  }}
                                >
                                  <b>Total:</b> {total}
                                  {isMax && " (maior)"}
                                  {isMin && " (menor)"}
                                </Typography>
                                <Divider sx={{ my: 0.5 }} />
                              </CardComponent>
                            );
                          })}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Paper>
          ))}
        {exhibition && judgesDurations.length > 0 && (
          <Paper
            sx={{
              p: { xs: 1.5, md: 2 },
              mt: 2,
              mb: 2,
              background: "#fafbfc",
              boxShadow: "none",
              borderRadius: 1,
            }}
            elevation={0}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              gutterBottom
              color="primary"
              sx={{ mb: 1 }}
            >
              Tempo de Julgamento dos Juízes
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <TableContainer component={Box}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {user?.typeId === 1 && exhibition?.finished && (
                      <TableCell sx={{ fontWeight: 700, color: "#666", width: "40px" }} />
                    )}
                    <TableCell sx={{ fontWeight: 700, color: "#666" }}>Juiz</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#666" }}>Início do Julgamento</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#666" }}>Fim do Julgamento</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#666" }}>Tempo Decorrido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {judgesDurations.map((judge) => {
                    const formattedStart = judge.start
                      ? dayjs(judge.start).format("DD/MM/YYYY HH:mm:ss")
                      : "-";
                    const formattedEnd = judge.end
                      ? dayjs(judge.end).format("DD/MM/YYYY HH:mm:ss")
                      : "";

                    let durationText = judge.duration;
                    let durationColor = "#666";
                    let durationWeight = 500;

                    if (judge.duration === "Não iniciado") {
                      durationColor = "#9e9e9e";
                    } else if (judge.start && !judge.end) {
                      durationText = `${judge.duration} (Em andamento)`;
                      durationColor = "#1976d2";
                      durationWeight = 700;
                    } else {
                      durationColor = "#2e7d32";
                      durationWeight = 700;
                    }

                    return (
                      <TableRow key={judge.judgeId}>
                        {user?.typeId === 1 && exhibition?.finished && (
                          <TableCell sx={{ width: "40px", padding: "0px 8px" }}>
                            <Tooltip title="Certificado Juizes" placement="top">
                              <IconButton
                                onClick={() => handleGenerateJudgeCertificate(judge.judgeId)}
                                size="small"
                                color="primary"
                              >
                                <WorkspacePremiumOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 500 }}>{judge.judgeName}</TableCell>
                        <TableCell>{formattedStart}</TableCell>
                        <TableCell>{formattedEnd}</TableCell>
                        <TableCell sx={{ color: durationColor, fontWeight: durationWeight }}>
                          {durationText}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </>
  );
};
